import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useFormik } from 'formik'
import * as Yup from 'yup'

import FormError from '../../styled/form-error'
import { axios, context as AuthContext } from '../../context/auth'

import InputGroup from '../../styled/input-group'
import AuthFormContainer from '../../styled/auth-form-container'
import { PrimaryButton } from '../../styled/button'

import { pageView, PAGES } from '../../utils/analytics-tracker'

const NewPasswordSchema = Yup.object().shape({
  password: Yup.string().min(6).max(128).required(),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password'), null], 'Passwords must match')
})

const NewPassword = () => {
  const navigate = useNavigate()
  const { token } = useParams()
  const [resetSuccess, setResetSuccess] = useState(false)
  const { refreshUser } = useContext(AuthContext)

  const { values, errors, touched, handleBlur, handleChange, handleSubmit, setErrors } = useFormik({
    initialValues: {
      password: '',
      confirmPassword: ''
    },
    validationSchema: NewPasswordSchema,
    onSubmit: (formValues) => submit(formValues)
  })

  useEffect(() => {
    pageView(PAGES.PASSWORD_NEW)
  }, ['-'])

  const submit = useCallback(async (values) => {
    try {
      const response = await axios.post('/auth/password-reset', {
        token,
        password: values.password,
        confirmPassword: values.confirmPassword
      })

      // Store JWT tokens from response headers
      if (response.headers['x-access-token']) {
        window.sessionStorage.setItem('accessToken', response.headers['x-access-token'])
      }
      if (response.headers['x-refresh-token']) {
        window.localStorage.setItem('refreshToken', response.headers['x-refresh-token'])
      }

      // Refresh user data
      await refreshUser()
      setResetSuccess(true)
      setTimeout(() => {
        navigate('/songs')
      }, 3000)
    } catch (err) {
      console.error('Password reset error:', err)
      if (err.response?.status === 400) {
        // Handle express-validator errors
        if (err.response.data?.errors) {
          const formErrors = {}
          err.response.data.errors.forEach(error => {
            formErrors[error.param] = error.msg
          })
          setErrors(formErrors)
        } else if (err.response.data?.msg) {
          // Handle general error message
          setErrors({ password: err.response.data.msg })
        } else {
          // Handle field-specific errors
          setErrors(err.response.data)
        }
      } else {
        setErrors({ password: 'An error occurred. Please try again.' })
      }
    }
  }, [token, refreshUser, navigate, setErrors])

  if (resetSuccess) {
    return (
      <AuthFormContainer>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✓</div>
          <h2 style={{ color: '#4CAF50', marginBottom: '1rem' }}>Password Reset Successful!</h2>
          <p style={{ color: '#666', lineHeight: '1.6', marginBottom: '1.5rem' }}>
            Your password has been successfully reset and you're now logged in.
          </p>
          <p style={{ color: '#999', fontSize: '0.9rem' }}>
            Redirecting you to your songs in 3 seconds...
          </p>
        </div>
      </AuthFormContainer>
    )
  }

  return (
    <>
      <h1>Create New Password</h1>
      <p style={{ color: '#666', marginBottom: '2rem', textAlign: 'center' }}>
        Please enter your new password below. Make sure it's at least 6 characters long.
      </p>
      <AuthFormContainer>
        <InputGroup>
          <label htmlFor='password'>New Password: </label>
          <input
            type='password'
            name='password'
            value={values.password}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder='Enter new password'
          />
          {touched.password && errors.password && <FormError>{errors.password}</FormError>}
          <label htmlFor='confirmPassword'>Confirm New Password: </label>
          <input
            type='password'
            name='confirmPassword'
            value={values.confirmPassword}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder='Re-enter new password'
          />
          {touched.confirmPassword && errors.confirmPassword && <FormError>{errors.confirmPassword}</FormError>}
          <PrimaryButton type='submit' onClick={handleSubmit}>Reset Password</PrimaryButton>
        </InputGroup>
      </AuthFormContainer>
    </>
  )
}

export default NewPassword
